import { inject, Injectable } from '@angular/core';
import { DialogZoomHelpComponent } from '@app/main/dashboard/dialogs/dialog-zoom-help/dialog-zoom-help.component';
import { DialogTrackerService } from '@app/core/services/dialog-tracker/dialog-tracker.service';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { LocalStorageService } from '@app/core/services/local-storage/local-storage.service';
import { DeviceService } from '@app/core/services/device/device.service';

interface IZoomHelpDialogParams {
  marginTop: string;
  appendTo?: Element;
}

@Injectable({ providedIn: 'root' })
export class ChartZoomHelpService {
  private readonly localStoreKey = 'zoom-help-dialog-blocked';
  private localStorageService: LocalStorageService = inject(LocalStorageService);
  private dialogTrackerService = inject(DialogTrackerService);
  private deviceService = inject(DeviceService);

  private dialogRef?: DynamicDialogRef<DialogZoomHelpComponent>;
  private component?: DialogZoomHelpComponent;

  private defaultZoomHelpDialogParams: IZoomHelpDialogParams = {
    marginTop: '2.75rem',
  };

  public handleZoomHelpDialog(params: Partial<IZoomHelpDialogParams> = {}) {
    if (this.deviceService.isTouchDevice() || this.getZoomHelpDialogBlocked()) return;

    if (!this.dialogRef) {
      this.dialogRef = this.openZoomHelpDialog(params);
      this.dialogRef.onClose.subscribe((result: boolean) => {
        if (result) {
          this.setZoomHelpDialogBlocked(true);
        }

        this.clearRefs();
      });
      this.dialogRef.onChildComponentLoaded.subscribe(component => {
        this.component = component;
      });
    } else if (this.component) {
      this.component.restartTimers();
    }
  }

  public deleteZoomHelpDialogBlocked() {
    this.localStorageService.removeFromLS(this.localStoreKey);
  }

  private getZoomHelpDialogBlocked(): boolean {
    return this.localStorageService.getFromLS(this.localStoreKey) || false;
  }

  private setZoomHelpDialogBlocked(value: boolean) {
    this.localStorageService.saveToLS(this.localStoreKey, value);
  }

  private clearRefs() {
    this.dialogRef = undefined;
    this.component = undefined;
  }

  private openZoomHelpDialog(params: Partial<IZoomHelpDialogParams>) {
    const paramsForDialog: IZoomHelpDialogParams = {
      ...this.defaultZoomHelpDialogParams,
      ...params,
    };

    return this.dialogTrackerService.regAndOpen(DialogZoomHelpComponent, {
      ...this.dialogTrackerService.getBaseDialogConfig(),
      modal: false,
      focusTrap: false,
      keepInViewport: false,
      appendTo: paramsForDialog.appendTo || document.querySelector('.zoom-help-dialog-host'),
      maskStyleClass: `!absolute inset-0 !z-1 mt-[${paramsForDialog.marginTop}] !h-auto`,
      position: 'top',
      width: undefined,
    });
  }
}
